import * as React from "react";
import { UitkCard, UitkCardContent, UitkCardMedia, UitkCardTextSegment } from "uitk-react-cards";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TopCitiesProps } from "../typings";
import { UitkFigure, UitkImage, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { inject } from "mobx-react";
import { ThumborStore } from "stores/thumbor/ThumborStore";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { LocalizedText } from "bernie-l10n";
import { CityDiscovery, PointOfInterest } from "typings/microserviceModels/top-cities-flex-module";
import TruncatedText from "shiitake";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";
import { AttributionText, getAttributionAltText } from "components/shared/AttributionText/AttributionText";
import { UitkHeading, UitkText } from "uitk-react-text";

const ButtonTracked = FlexClickTracker(UitkSecondaryButton);

export const TopCitiesDestinationDiscovery = (props: TopCitiesProps) => {
  const flexComponent = props.flexComponent;
  const moduleName = flexComponent.name;
  const keyHelper = new ItemKeyHelper("top-cities");
  const model = flexComponent.model;

  // Return null if not DD view since Hotel-Destination pages also have this module and we don't yet support it
  if (!flexComponent || !model || !model.topCities || model.empty || model.view !== "destination-discovery") {
    return null;
  }

  return (
    <div
      className="TopCitiesDestinationDiscovery uitk-cell all-b-margin-six"
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <div className="all-b-margin-three">
        {Boolean(props.context.searchContext.location.localizedName) && (
          <UitkHeading size={500} level={2}>
            <LocalizedText message="topCities.title" data={[props.context.searchContext.location.localizedName]} />
          </UitkHeading>
        )}
      </div>
      <div className="topCitiesContainer">
        {model.topCities.map((city, cityIndex) => (
          <CityCard
            key={keyHelper.next()}
            city={city}
            index={cityIndex}
            context={props.context}
            moduleName={moduleName}
          />
        ))}
      </div>
    </div>
  );
};

export const CityCard = inject("thumbor")(
  (props: {
    city: CityDiscovery;
    index: number;
    thumbor?: ThumborStore;
    context: ExtendedContextStore;
    moduleName: string;
  }) => {
    const { thumbor, city, context, moduleName } = props;
    const { name, shortDescription, exploreUrl, hotelUrl, affinity, images } = city;
    const poiKeyHelper = new ItemKeyHelper("top-cities-poi");
    const cityImageUrl =
      thumbor &&
      images?.[0]?.url &&
      thumbor.safelyGenerateThumborizedImageUrl(images[0].url, 400, 200, context, undefined, true, 100);

    const image = images && images[0];
    const imageAttribution = image?.attribution;
    const attributionLicense = imageAttribution?.license;
    const { formatText } = useLocalization();

    const imageAlt = image?.alt || "";

    const source = image?.source;
    const origin = image?.origin || "";
    const attributionUrl = imageAttribution?.url;
    const attributionRequired = attributionLicense?.attributionRequired;
    const authorName = imageAttribution?.authorName;
    const authorUrl = imageAttribution?.authorUrl;
    const licenseSuite = attributionLicense?.suite;
    const licenseName = attributionLicense?.name;
    const licenseUrl = attributionLicense?.url;

    const affinities = (knownFor: string[]) => {
      if (!knownFor.length) {
        return null;
      }

      const messageType = {
        1: <LocalizedText message="topCities.knownFor1" data={[knownFor[0]]} />,
        2: <LocalizedText message="topCities.knownFor2" data={[knownFor[0], knownFor[1]]} />,
        3: <LocalizedText message="topCities.knownFor3" data={[knownFor[0], knownFor[1], knownFor[2]]} />
      };

      return messageType[knownFor.length];
    };

    return (
      <UitkCard className="topCitiesCard all-l-margin-two">
        <UitkCardContent className="topCitiesCardContentContainer">
          <UitkFigure>
            <UitkCardMedia>
              <TrackedLink
                title={`Open ${city.name} page`}
                className="uitk-attribution-link"
                moduleName="topCities"
                href={city.exploreUrl}
                rffr="topCities.exploreCity"
              >
                {cityImageUrl && cityImageUrl.length > 0 && (
                  <UitkImage
                    alt={getAttributionAltText(
                      source,
                      origin,
                      name,
                      imageAlt,
                      authorName,
                      attributionLicense,
                      attributionUrl
                    )}
                    src={cityImageUrl}
                    placeholderImage={true}
                  />
                )}
                <UitkScrim type={UitkScrimType.BOTTOM}>
                  <div className={attributionRequired ? "topCities-hero-caption-container" : ""}>
                    <div className="truncate">
                      <UitkText size={500} weight="bold">
                        {name}
                      </UitkText>
                    </div>
                    <UitkText size={300}>{affinities(affinity)}</UitkText>
                  </div>
                </UitkScrim>
              </TrackedLink>
              <AttributionText
                source={source}
                origin={origin}
                attribUrl={attributionUrl}
                authorName={authorName}
                authorUrl={authorUrl}
                licenseSuite={licenseSuite}
                licenseName={licenseName}
                licenseUrl={licenseUrl}
              />
            </UitkCardMedia>
          </UitkFigure>

          <UitkCardTextSegment className="first">
            <TruncatedText
              className="uitk-type-300 all-b-margin-four topCitiesDescription"
              lines={4}
              overflowNode={"\u2026\u0022"}
              tagName="p"
            >
              {shortDescription}
            </TruncatedText>
            <div className="topCitiesSecondHalf">
              {Boolean(city?.pois?.length) && (
                <div>
                  <span>
                    <UitkHeading size={300} level={2}>
                      <LocalizedText message="topCities.reasonsToVisit" />
                    </UitkHeading>
                  </span>
                  <ul className="no-bullet all-b-margin-four">
                    {city?.pois?.map((poi: PointOfInterest, i: number) => (
                      <li
                        className={`all-t-margin-three topCitiesPoi ${
                          i > 0 ? "all-t-margin-two" : "all-t-margin-four"
                        }`}
                        key={poiKeyHelper.next()}
                      >
                        {poi?.image?.[0]?.url ? (
                          <TrackedLink
                            title={`Open ${poi.name}`}
                            className="topCitiesPoiLink"
                            moduleName="topCities"
                            rfrr="cityPOI"
                            href={poi.url}
                          >
                            <CityPOI poi={poi} context={context} />
                          </TrackedLink>
                        ) : (
                          <CityPOI poi={poi} context={context} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </UitkCardTextSegment>
        </UitkCardContent>
        <div className="topCitiesButtonWrapper all-x-padding-four all-y-padding-four">
          <ButtonTracked
            data-testid="exploreCityContainer"
            className="topCitiesCardButton"
            linkName="Explore city click event in the Top Cities"
            href={exploreUrl}
            text={formatText("topCities.exploreCity")}
            tag="a"
            rfrr="exploreCity"
            moduleName={moduleName}
            aria-label={formatText("destinationDiscoveryMap.title.mobile", city.name)}
          />
          <ButtonTracked
            data-testid="findHotelsContainer"
            className="topCitiesCardButton"
            linkName="Find Hotels click event in the Top Cities"
            href={hotelUrl}
            text={formatText("topCities.findHotels")}
            tag="a"
            rfrr="findHotels"
            moduleName={moduleName}
            aria-label={formatText("destinationCity.topCities.findHotels", city.name)}
          />
        </div>
      </UitkCard>
    );
  }
);

export const CityPOI = inject("thumbor")(
  (props: { poi: PointOfInterest; thumbor?: ThumborStore; context: ExtendedContextStore }) => {
    const { thumbor, poi, context } = props;

    return (
      <div className="topCitiesPoiCardMedia">
        <div className="topCitiesPoiImageContainer all-r-margin-four">
          {Boolean(poi?.image?.url?.length) && thumbor ? (
            <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
              <UitkImage
                className="topCitiesPoiImage"
                alt={poi.name}
                src={thumbor.safelyGenerateThumborizedImageUrl(poi.image.url, 48, 48, context, undefined, true, 100)}
                placeholderImage={true}
              />
            </UitkFigure>
          ) : (
            <div className="TopCitiesPoiPlaceholder" />
          )}
        </div>
        <UitkText size={300}> {poi.name} </UitkText>
      </div>
    );
  }
);

export default TopCitiesDestinationDiscovery;
